import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  fetchSharesData,
  exportAllShares,
  exportSelectedShares,
} from "../../sdk/shares/shares";
import { downloadCSV, downloadPDF } from "../../hooks/download";
import ExportShares from "../../components/export/ExportShares";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";
import Pagination from "../../shared/Pagination";
import { ShareRowSkeleton } from "../../components/loaders/Loading";

const Shares = () => {
  const [shares, setShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);

  const [displayedPage, setDisplayedPage] = useState(1);
  let [currentPage, setCurrentPage] = useState(1);
  const [totalShares, setTotalShares] = useState([]);
  let [totalPages, setTotalPages] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(20);

  const navigate = useNavigate();

  const navigateToSharesView = (memberId) => {
    navigate(`/dashboard/shares/${memberId}`);
  };

  const getSharesData = async () => {
    try {
      setSharesLoading(true);
      const response = await fetchSharesData(currentPage, limit, query, status);

      setShares(response?.data?.data);
      setDisplayedPage(response?.data?.meta?.page);
      setTotalPages(response?.data?.meta?.totalPages);
      setTotalShares(response?.data?.meta?.total);
    } catch (error) {
      console?.error("Failed to fetch shares data:", error);
      setShares([]);
    } finally {
      setSharesLoading(false);
    }
  };

  useEffect(() => {
    getSharesData();
  }, [currentPage, limit, query, status]);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const handleRefetch = async () => {
    setRefreshLoading(true);
    await getSharesData();
    setRefreshLoading(false);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleLimitChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setLimit(newItemsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
  };

  const SKELETON_ROWS = 6;

  const renderPages = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(totalPages, 3); i++) {
      pages.push(
        <div
          key={i}
          className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
            currentPage === i
              ? "bg-[#074073] text-white"
              : "bg-[#F5F5F5] text-[#074073]"
          }`}
          onClick={() => handlePageChange(i)}
        >
          <p>{i}</p>
        </div>,
      );
    }

    return pages;
  };

  const [selectedShares, setSelectedShares] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleCheckboxChange = (event, share) => {
    if (event.target.checked) {
      setSelectedShares((prevSelectedShares) => [...prevSelectedShares, share]);
    } else {
      setSelectedShares((prevSelectedShares) =>
        prevSelectedShares.filter(
          (selectedShare) => selectedShare.memberId !== share.memberId,
        ),
      );
    }
  };

  const handleSelectAllChange = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    setSelectedShares(isChecked ? shares : []);
  };

  const handleExportAllShares = async (exportType, limit, status) => {
    const response = await exportAllShares(exportType, limit, status);
    exportType === "pdf"
      ? await downloadPDF(response, "shares.pdf")
      : await downloadCSV(response, "shares.csv");
  };

  const handleExportShares = async (exportType) => {
    try {
      const response = await exportSelectedShares(exportType, selectedShares);
      exportType === "pdf"
        ? await downloadPDF(response, "selected_members.pdf")
        : await downloadCSV(response, "selected_members.csv");
      setSelectAll(false);
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Belite || Transactions</title>
      </Helmet>

      <div className="w-[100%] px-[15px] h-full overflow-y-auto">
        <div className="mt-[10px]">
          <p className="text-[#074073] text-[20px] font-bold">INVESTMENT</p>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="relative items-center justify-center">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="13"
                viewBox="0 0 14 13"
                fill="none"
              >
                <circle
                  cx="5.35294"
                  cy="5.35294"
                  r="4.85294"
                  stroke="#999999"
                />
                <line
                  x1="9.52933"
                  y1="8.82294"
                  x2="13.3529"
                  y2="12.6465"
                  stroke="#999999"
                />
              </svg>
            </span>
            <input
              type="text"
              value={query}
              onChange={handleSearch}
              placeholder="Search member ID or member name"
              className="h-[35px] pl-10 border-[1px] border-[#CCCCCC] text-[14px] rounded-[2px] w-[36vw] px-[10px] focus:outline-none"
            />
          </div>
          <div className="flex gap-4">
            <button
              className="block text-white bg-[#074073] focus:outline-none focus:ring-[#074073] rounded-[3px] text-center w-[10vw] h-[40px]"
              type="button"
              onClick={handleRefetch}
            >
              <div className="flex items-center justify-center gap-2">
                {!refreshLoading && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="29"
                    height="29"
                    viewBox="0 0 29 29"
                    fill="none"
                  >
                    <path
                      d="M14.5931 22.9583C12.232 22.9583 10.2318 22.1395 8.59252 20.5018C6.95402 18.8633 6.13477 16.8639 6.13477 14.5036C6.13477 12.1433 6.95402 10.1431 8.59252 8.50303C10.2318 6.86211 12.232 6.04166 14.5931 6.04166C16.0294 6.04166 17.3739 6.38281 18.6265 7.06511C19.88 7.74742 20.8901 8.69999 21.657 9.92282V6.04166H22.8653V12.3612H16.5458V11.1529H21.0166C20.3875 9.9486 19.5022 8.99684 18.3607 8.29761C17.2208 7.5992 15.965 7.24999 14.5931 7.24999C12.5792 7.24999 10.8674 7.95485 9.45768 9.36457C8.04796 10.7743 7.3431 12.4861 7.3431 14.5C7.3431 16.5139 8.04796 18.2257 9.45768 19.6354C10.8674 21.0451 12.5792 21.75 14.5931 21.75C16.1438 21.75 17.5434 21.3069 18.7921 20.4208C20.0407 19.5347 20.9167 18.3667 21.4202 16.9167H22.7022C22.1698 18.7259 21.1576 20.1852 19.6657 21.2944C18.1738 22.4037 16.4829 22.9583 14.5931 22.9583Z"
                      fill="white"
                    />
                  </svg>
                )}
                {refreshLoading && (
                  <svg
                    aria-hidden="true"
                    className="w-[20px] h-[20px] text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                )}
                <p className="text-[#FFF] font-normal text-[16px]">REFRESH</p>
              </div>
            </button>
            <PermissionGate required={[PERMISSIONS?.SH?.EXPORT]}>
              <ExportShares
                handleExportAllShares={handleExportAllShares}
                handleExportMembers={handleExportShares}
                selectedMembers={selectedShares}
                setSelectedMembers={setSelectedShares}
                limit={limit}
                status={status}
              />
            </PermissionGate>
          </div>
        </div>

        <div className="bg-[#FFF] w-full">
          <div className="mt-3 h-[63px] flex px-[10px] w-full bg-[#F7FFD7]">
            <div className="flex items-center gap-[10px] justify-center">
              <div className="flex-col">
                <p className="text-[14px] font-normal text-end text-[#000] mt-1">
                  Status
                </p>
              </div>
              <div className="flex-col">
                <div className="flex items-center gap-[15px] w-[253px]">
                  <select
                    name="status"
                    id="status"
                    className="w-[200px] border border-gray-300 h-[30px] px-[10px] focus:outline-none"
                    value={status}
                    onChange={handleStatusChange}
                  >
                    <option value="">Select Status</option>
                    <option value="active">Active</option>
                    <option value="compliant">Compliant</option>
                    <option value="inactive">Inactive</option>
                    <option value="missed_deposit">Missed Deposit</option>
                    <option value="penalized">Penalized</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="h-[44px] bg-[#E3F8FF] w-full flex items-center mt-[2px]">
            <input
              type="checkbox"
              name="share"
              id="share"
              className="w-[10%]"
              checked={selectAll}
              onChange={handleSelectAllChange}
            />
            <p className="w-[25%] text-[14px] text-[#000000]">MEMBER ID</p>
            <p className="w-[40%] text-[14px] text-[#000000]">NAME OF MEMBER</p>
            <p className="w-[25%] text-[14px] text-[#000000]">ACCOUNT TYPE</p>
            <p className="w-[25%] text-[14px] text-[#000000]">
              CURRENT BALANCE
            </p>

            <div className="w-[25%] flex items-center gap-[5px]">
              <p className="text-[14px] text-[#000000]">STATUS</p>
            </div>
          </div>

          <div>
            {sharesLoading &&
              Array?.from({ length: SKELETON_ROWS })?.map((_, index) => (
                <ShareRowSkeleton key={`share-skeleton-${index}`} />
              ))}
            {!sharesLoading &&
              shares?.length > 0 &&
              shares?.map((share) => (
                <div
                  key={share?.customer?.public_id}
                  className="h-[44px] !text-[14px] bg-[#fff] w-full flex items-center"
                >
                  <input
                    type="checkbox"
                    name="share"
                    id="share"
                    className="w-[10%]"
                    onChange={(event) => handleCheckboxChange(event, share)}
                    checked={selectedShares.some(
                      (selectedShare) =>
                        selectedShare?.memberId === share?.customer?.public_id,
                    )}
                  />

                  <PermissionGate required={[PERMISSIONS?.SH?.VIEW_DETAILS]}>
                    <p
                      onClick={() =>
                        navigateToSharesView(share?.customer?.public_id)
                      }
                      className="w-[25%] wrap text-[#037BD1] cursor-pointer !text-[14px] text-[#000000]"
                    >
                      {share?.customer?.public_id}
                    </p>
                  </PermissionGate>

                  <PermissionGate
                    required={[PERMISSIONS?.SH?.VIEW_DETAILS]}
                    fallback={
                      <p className="w-[25%] wrap text-[#037BD1] !text-[14px] text-[#000000] cursor-not-allowed">
                        {share?.customer?.public_id}
                      </p>
                    }
                  />

                  <p className="w-[40%] !text-[14px] text-[#000000]">
                    {share?.customer?.firstname
                      ?.split(" ")
                      ?.map(
                        (name) =>
                          name.charAt(0).toUpperCase() +
                          name.slice(1).toLowerCase(),
                      )
                      ?.join(" ")}{" "}
                    {share?.customer?.middlename
                      ?.split(" ")
                      ?.map(
                        (name) =>
                          name.charAt(0).toUpperCase() +
                          name.slice(1).toLowerCase(),
                      )
                      ?.join(" ")}
                    {share?.customer?.lastname
                      ?.split(" ")
                      ?.map(
                        (name) =>
                          name.charAt(0).toUpperCase() +
                          name.slice(1).toLowerCase(),
                      )
                      ?.join(" ")}
                  </p>

                  <p className="w-[25%] !text-[14px] text-[#000000]">
                    {share?.product?.name}
                  </p>

                  <p className="w-[25%] !text-[14px] text-[#000000]">
                    KES &nbsp;
                    {Number(share?.balance || "0.00")?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>

                  <div className="flex items-center gap-[5px] w-[25%]">
                    {share?.status === "compliant" && (
                      <svg
                        width="11"
                        height="12"
                        viewBox="0 0 11 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#clip0_15701_824)">
                          <path
                            d="M5.50018 1.53274C4.54224 1.53297 3.60972 1.84102 2.84014 2.41147C2.07056 2.98191 1.50466 3.78456 1.22589 4.70105C0.947124 5.61753 0.970239 6.59934 1.29183 7.5017C1.61342 8.40405 2.21647 9.17917 3.01204 9.71277C3.80761 10.2464 4.75359 10.5102 5.71049 10.4653C6.66738 10.4205 7.58454 10.0693 8.3267 9.46364C9.06887 8.85796 9.59676 8.02982 9.83253 7.10134C10.0683 6.17286 9.99946 5.19319 9.63618 4.3068C9.58422 4.18026 9.58465 4.03826 9.63738 3.91204C9.69011 3.78581 9.79083 3.68571 9.91737 3.63374C10.0439 3.58178 10.1859 3.58221 10.3121 3.63494C10.4384 3.68767 10.5385 3.78839 10.5904 3.91493C11.0721 5.09071 11.1319 6.397 10.7595 7.61184C10.3871 8.82668 9.60562 9.87511 8.54776 10.579C7.48991 11.2829 6.22098 11.5988 4.95659 11.473C3.69221 11.3472 2.51037 10.7876 1.6119 9.88909C0.713429 8.99062 0.15376 7.80878 0.0279935 6.54439C-0.0977734 5.28001 0.218121 4.01107 0.921996 2.95322C1.62587 1.89537 2.67431 1.11386 3.88915 0.741487C5.10398 0.369115 6.41028 0.428854 7.58606 0.910554C7.64876 0.93624 7.70579 0.974024 7.75389 1.02175C7.80199 1.06948 7.84022 1.12621 7.86639 1.18871C7.89257 1.25121 7.90617 1.31825 7.90644 1.38601C7.9067 1.45377 7.89362 1.52091 7.86793 1.58362C7.84225 1.64632 7.80446 1.70335 7.75674 1.75145C7.70901 1.79955 7.65228 1.83778 7.58978 1.86395C7.52728 1.89012 7.46024 1.90373 7.39248 1.904C7.32472 1.90426 7.25757 1.89118 7.19487 1.86549C6.65699 1.64543 6.08134 1.53241 5.50018 1.53274ZM9.81768 1.68399C9.91424 1.78067 9.96848 1.91173 9.96848 2.04837C9.96848 2.18501 9.91424 2.31606 9.81768 2.41274L7.02437 5.20674C7.21434 5.57083 7.26766 5.99091 7.17469 6.39091C7.08172 6.79092 6.84859 7.14441 6.51753 7.3874C6.18646 7.63039 5.77933 7.7468 5.36985 7.71557C4.96037 7.68434 4.57561 7.50752 4.28522 7.21714C3.99484 6.92675 3.81802 6.54199 3.78679 6.13251C3.75556 5.72303 3.87198 5.3159 4.11496 4.98484C4.35795 4.65377 4.71145 4.42065 5.11145 4.32767C5.51146 4.2347 5.93153 4.28803 6.29562 4.47799L9.08893 1.68399C9.18561 1.58743 9.31667 1.5332 9.45331 1.5332C9.58995 1.5332 9.721 1.58743 9.81768 1.68399ZM4.81268 6.00149C4.81268 6.18383 4.88512 6.3587 5.01405 6.48763C5.14298 6.61656 5.31785 6.68899 5.50018 6.68899C5.68252 6.68899 5.85739 6.61656 5.98632 6.48763C6.11525 6.3587 6.18768 6.18383 6.18768 6.00149C6.18768 5.81916 6.11525 5.64429 5.98632 5.51536C5.85739 5.38642 5.68252 5.31399 5.50018 5.31399C5.31785 5.31399 5.14298 5.38642 5.01405 5.51536C4.88512 5.64429 4.81268 5.81916 4.81268 6.00149Z"
                            fill="#0C722F"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_15701_824">
                            <rect
                              width="11"
                              height="11"
                              fill="white"
                              transform="translate(0 0.5)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    )}
                    {share?.status?.toLowerCase() === "active" && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="12"
                        viewBox="0 0 13 12"
                        fill="none"
                      >
                        <g clip-path="url(#clip0_6917_381)">
                          <path
                            d="M6.5 12C9.81371 12 12.5 9.31371 12.5 6C12.5 2.68629 9.81371 0 6.5 0C3.18629 0 0.5 2.68629 0.5 6C0.5 9.31371 3.18629 12 6.5 12Z"
                            fill="#0C722F"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_6917_381">
                            <rect
                              width="12"
                              height="12"
                              fill="white"
                              transform="translate(0.5)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    )}
                    {share?.status === "inactive" && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 0C6.55078 0 7.08203 0.0703125 7.59375 0.210938C8.10547 0.351562 8.58203 0.554688 9.02344 0.820312C9.46484 1.08594 9.86914 1.39844 10.2363 1.75781C10.6035 2.11719 10.918 2.52148 11.1797 2.9707C11.4414 3.41992 11.6426 3.89844 11.7832 4.40625C11.9238 4.91406 11.9961 5.44531 12 6C12 6.55078 11.9297 7.08203 11.7891 7.59375C11.6484 8.10547 11.4453 8.58203 11.1797 9.02344C10.9141 9.46484 10.6016 9.86914 10.2422 10.2363C9.88281 10.6035 9.47852 10.918 9.0293 11.1797C8.58008 11.4414 8.10156 11.6426 7.59375 11.7832C7.08594 11.9238 6.55469 11.9961 6 12C5.44922 12 4.91797 11.9297 4.40625 11.7891C3.89453 11.6484 3.41797 11.4453 2.97656 11.1797C2.53516 10.9141 2.13086 10.6016 1.76367 10.2422C1.39648 9.88281 1.08203 9.47852 0.820312 9.0293C0.558594 8.58008 0.357422 8.10156 0.216797 7.59375C0.0761719 7.08594 0.00390625 6.55469 0 6C0 5.44922 0.0703125 4.91797 0.210938 4.40625C0.351562 3.89453 0.554688 3.41797 0.820312 2.97656C1.08594 2.53516 1.39844 2.13086 1.75781 1.76367C2.11719 1.39648 2.52148 1.08203 2.9707 0.820312C3.41992 0.558594 3.89844 0.357422 4.40625 0.216797C4.91406 0.0761719 5.44531 0.00390625 6 0ZM9.5332 4.01367L8.73633 3.2168L4.875 7.07812L3.26367 5.4668L2.4668 6.26367L4.875 8.67188L9.5332 4.01367Z"
                          fill="#0C722F"
                        />
                      </svg>
                    )}
                    {share?.status === "missed_deposit" && (
                      <svg
                        width="15"
                        height="14"
                        viewBox="0 0 15 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7.28125 7.72476V3.85938V7.72476ZM13.5625 7C13.5625 8.66589 12.9007 10.2636 11.7228 11.4415C10.5448 12.6195 8.94714 13.2812 7.28125 13.2812C5.61536 13.2812 4.0177 12.6195 2.83974 11.4415C1.66177 10.2636 1 8.66589 1 7C1 5.33411 1.66177 3.73645 2.83974 2.55849C4.0177 1.38052 5.61536 0.71875 7.28125 0.71875C8.94714 0.71875 10.5448 1.38052 11.7228 2.55849C12.9007 3.73645 13.5625 5.33411 13.5625 7Z"
                          fill="#FAB32B"
                        />
                        <path
                          d="M7.28125 7.72476V3.85937M13.5625 7C13.5625 8.66589 12.9007 10.2636 11.7228 11.4415C10.5448 12.6195 8.94714 13.2812 7.28125 13.2812C5.61536 13.2812 4.0177 12.6195 2.83974 11.4415C1.66177 10.2636 1 8.66589 1 7C1 5.33411 1.66177 3.73645 2.83974 2.55849C4.0177 1.38052 5.61536 0.71875 7.28125 0.71875C8.94714 0.71875 10.5448 1.38052 11.7228 2.55849C12.9007 3.73645 13.5625 5.33411 13.5625 7Z"
                          stroke="white"
                          stroke-width="1.3"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M7.7642 9.89724C7.7642 10.0254 7.71329 10.1483 7.62268 10.2389C7.53207 10.3295 7.40917 10.3804 7.28102 10.3804C7.15288 10.3804 7.02998 10.3295 6.93937 10.2389C6.84876 10.1483 6.79785 10.0254 6.79785 9.89724C6.79785 9.76909 6.84876 9.64619 6.93937 9.55558C7.02998 9.46497 7.15288 9.41406 7.28102 9.41406C7.40917 9.41406 7.53207 9.46497 7.62268 9.55558C7.71329 9.64619 7.7642 9.76909 7.7642 9.89724Z"
                          fill="#FAB32B"
                          stroke="white"
                        />
                      </svg>
                    )}
                    {share?.status === "penalized" && (
                      <svg
                        width="13"
                        height="14"
                        viewBox="0 0 13 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.5 1.3125C5.37512 1.3125 4.2755 1.64607 3.3402 2.27102C2.40489 2.89597 1.67591 3.78423 1.24544 4.82349C0.814964 5.86274 0.702333 7.00631 0.921786 8.10958C1.14124 9.21284 1.68292 10.2263 2.47833 11.0217C3.27374 11.8171 4.28716 12.3588 5.39043 12.5782C6.49369 12.7977 7.63726 12.685 8.67651 12.2546C9.71577 11.8241 10.604 11.0951 11.229 10.1598C11.8539 9.2245 12.1875 8.12488 12.1875 7C12.1858 5.49211 11.586 4.04647 10.5198 2.98023C9.45353 1.91399 8.00789 1.31422 6.5 1.3125ZM6.5 11.875C5.20707 11.875 3.9671 11.3614 3.05286 10.4471C2.13862 9.53291 1.625 8.29293 1.625 7C1.625 5.70707 2.13862 4.46709 3.05286 3.55285C3.9671 2.63861 5.20707 2.125 6.5 2.125V7L9.94541 10.4454C9.49361 10.8989 8.95664 11.2587 8.36536 11.504C7.77409 11.7494 7.14016 11.8754 6.5 11.875Z"
                          fill="#0C722F"
                        />
                      </svg>
                    )}
                    {share?.status === "Sold" && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="12"
                        viewBox="0 0 13 12"
                        fill="none"
                      >
                        <g clip-path="url(#clip0_6917_381)">
                          <path
                            d="M6.5 12C9.81371 12 12.5 9.31371 12.5 6C12.5 2.68629 9.81371 0 6.5 0C3.18629 0 0.5 2.68629 0.5 6C0.5 9.31371 3.18629 12 6.5 12Z"
                            fill="#FF5757"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_6917_381">
                            <rect
                              width="12"
                              height="12"
                              fill="white"
                              transform="translate(0.5)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    )}
                    <p className="!text-[14px] text-[#000000]">
                      {share?.status
                        ?.split("   ")
                        ?.map(
                          (name) =>
                            name?.charAt(0)?.toUpperCase() +
                            name?.slice(1)?.toLowerCase(),
                        )
                        ?.join(" ")}
                    </p>
                  </div>
                </div>
              ))}
            {!sharesLoading && shares?.length === 0 && (
              <div className="px-[20px] flex items-center mt-[20px]">
                <p className="text-[#074073] !text-[14px]">
                  No record of Investments
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="h-[70px] bg-white w-[97%] px-[20px] mx-auto mb-[20px] flex items-center justify-between">
          <div className="flex items-center gap-[20px]">
            <p className="text-[#074073] !text-[14px]">Rows per page</p>
            <select
              name="page"
              id="page"
              className="w-[71px] border-[1px] px-[5px] h-[30px] focus:outline-none"
              value={limit}
              onChange={handleLimitChange}
            >
              <option value="50">50</option>
              <option value="30">30</option>
              <option value="20">20</option>
              <option value="10">10</option>
            </select>
            <p className="text-[#074073] text-[14px]">
              {displayedPage} / {totalPages} ({totalShares} records)
            </p>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
};

export default Shares;
